import { useState } from "react";
import { Settings as SettingsIcon, Bell, Moon, Sun, Palette, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

export default function Settings() {
  const [settings, setSettings] = useState({
    notifications: true,
    soundAlerts: true,
    autoRefresh: true,
    darkMode: true,
  });

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">
          SETTINGS
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Customize your SP Wealth Tracker experience
        </p>
      </div>

      {/* Notification Settings */}
      <Card className="card-dark" data-testid="notification-settings">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5" />
            NOTIFICATIONS
          </CardTitle>
          <CardDescription>
            Configure how you receive alerts and updates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Push Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive browser notifications for price alerts
              </p>
            </div>
            <Switch
              checked={settings.notifications}
              onCheckedChange={() => handleToggle("notifications")}
              data-testid="toggle-notifications"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Sound Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Play sound when important alerts trigger
              </p>
            </div>
            <Switch
              checked={settings.soundAlerts}
              onCheckedChange={() => handleToggle("soundAlerts")}
              data-testid="toggle-sound"
            />
          </div>
        </CardContent>
      </Card>

      {/* Display Settings */}
      <Card className="card-dark" data-testid="display-settings">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Palette className="w-5 h-5" />
            DISPLAY
          </CardTitle>
          <CardDescription>
            Appearance and refresh settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Dark Mode</Label>
              <p className="text-sm text-muted-foreground">
                Use dark theme (recommended for trading)
              </p>
            </div>
            <Switch
              checked={settings.darkMode}
              onCheckedChange={() => handleToggle("darkMode")}
              data-testid="toggle-dark-mode"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Auto Refresh</Label>
              <p className="text-sm text-muted-foreground">
                Automatically refresh prices every 30 seconds
              </p>
            </div>
            <Switch
              checked={settings.autoRefresh}
              onCheckedChange={() => handleToggle("autoRefresh")}
              data-testid="toggle-auto-refresh"
            />
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card className="card-dark" data-testid="about-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Info className="w-5 h-5" />
            ABOUT
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Version</p>
              <p className="font-data">1.0.0</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">AI Models</p>
              <p className="font-data">GPT-5.2, Claude, Gemini</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Data Source</p>
              <p className="font-data">CoinGecko API</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Refresh Rate</p>
              <p className="font-data">30 seconds</p>
            </div>
          </div>

          <Separator />

          <div className="bg-ai/10 border border-ai/20 p-4">
            <h4 className="font-heading font-bold text-ai mb-2">AI-POWERED ANALYSIS</h4>
            <p className="text-sm text-muted-foreground">
              This app uses three AI models (OpenAI GPT-5.2, Claude Sonnet 4.5, and Gemini 3 Flash) 
              to analyze financial news from multiple sources. Each news item is processed through 
              all three models to provide comprehensive sentiment analysis and investment insights.
            </p>
          </div>

          <div className="text-center text-sm text-muted-foreground pt-4">
            <p>SP Wealth Tracker</p>
            <p>Built with AI-powered insights for smarter investments</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
